import { Camera, Scene, Texture, Renderer, Mesh, Program, PlaneGeometryBuffer, CylinderGeometryBuffer, BoxGeometryBuffer, SphereGeometryBuffer, FrameBuffer, Light, vec3 } from 'beam';
import Lenis from '../../helpers/Lenis'
import getPosition from '../../helpers/getPosition';

const DPR   = window.devicePixelRatio
const TOUCH = ('ontouchstart' in window) || (navigator.msMaxTouchPoints > 0);


export default class GLBlurNumber extends HTMLElement {

    constructor() {
        super();
        this.scroll = 0;
        this.center     = { x: 0, y: 0 };
        this.mouseObj   = { x: 0, y: 0 };
        this.pointer    = [0,0];
        this.cursor     = [0,0];
        this.mouseObjX  = 0;
        this.mouseObjY  = 0;
        this.uniforms   = {};
        this.defines    = {};
        this.time       = 0;
        this.resolution = [window.innerWidth, window.innerHeight];
        this.attachShadow({ mode: "open" });
        this.onSlotchange  = this.onSlotchange.bind(this);
        this.resize        = this.resize.bind(this);
        this.update        = this.update.bind(this);
        this.onPointerMove = this.onPointerMove.bind(this);
        this.titles = [];

        this.$canvas = document.createElement('canvas');
        this.ctx = this.$canvas.getContext('2d');
    }
    
    onSlotchange(e) {
        this.initialize();
    }

    connectedCallback() {

        this.style.position = 'relative';
        this.style.display  = 'block';
        this.shadowRoot.innerHTML = `
            <canvas style="top:0; left:0; position: absolute; width: 100%; pointer-events: none; background: transparent;"></canvas>
            <slot></slot>
        `;
        this.$slot = this.shadowRoot.querySelector('slot');
        this.$slot.addEventListener('slotchange', this.onSlotchange);
        Lenis?.on?.('scroll', e => {
            this.scroll = e.animatedScroll;
        });

    }
    
    disconnectedCallback() {
        this.$slot.addEventListener('slotchange', this.onSlotchange);
    }
    
    async initialize() {

        const $canvas = this.shadowRoot.querySelector('canvas');

        this.renderer = new Renderer({
            canvas:    $canvas,
            alpha:     true,
            premultipliedAlpha: true,
        });
        this.scene = new Scene();
        this.scene.uniforms = Object.assign(this.uniforms, this.scene.uniforms);
        this.scene.defines  = Object.assign(this.defines, this.scene.defines);
        this.camera = new Camera({
            left:          -1/2,
            right:          1/2,
            top:            1/2,
            bottom:        -1/2,
            near:           1,
            far:            4000.0,
            type:           'perspective',
            fov:            45,
            lookAt:         [0,0,0], 
            position:       [0,0,1000] ,
            cameraDistance: 2500,
            phi:            0.5,
            theta:          0.5,
        });
        this.plane          = new Mesh();		
        this.plane.geometry = new PlaneGeometryBuffer(this.renderer.gl, { width: 1, height: 1, depth: 1 });
        this.plane.material = new Program(this.renderer.gl, {
            depthTest:      false,
            depthWrite:     false,
            blend:          true,
            vertexShader:    /*glsl*/`
                #version 300 es
                precision highp float;
                precision highp int;
                in vec2 uv;
                in vec3 position;
                uniform mat4 uPMatrix;
                uniform mat4 uMVMatrix;
                uniform mat4 uMMatrix;
                out vec2 vUv;
                out vec3 vWorldPosition;
                void main(void) {
                    vUv              = uv;
                    vWorldPosition   = (uMMatrix * vec4(position, 1.0)).xyz;
                    gl_Position      = uPMatrix * uMVMatrix * vec4(position, 1.0);
                }`,
            fragmentShader:  /*glsl*/`
                #version 300 es
            
                #define ITERATIONS 20.0
                #define saturate(a) clamp( a, 0.0, 1.0 )
                #define ONEOVER_ITR  1.0 / ITERATIONS
                #define PI 3.141596
                #define GOLDEN_ANGLE 2.39996323
                
                precision highp float;
                precision highp int;
                uniform vec2 uMouse;             
                uniform sampler2D tMap;          
                uniform vec2 uResolution;        
                uniform float uTime;       
                uniform vec2 uBlurDirection;     
                uniform int useBlurNoise; 

                in vec2 vUv;
                out vec4 fragColor;

                float random(vec2 uv) {
                    return fract(sin(dot(uv, vec2(12.9898, 78.233))) * 43758.5453);
                }

                float animatedNoise(vec2 uv, float time) {
                    return random(uv + vec2(sin(time), cos(time))) * 0.5 + 0.5;
                }

                vec2 r(vec2  p) {
                    p = vec2(dot(p,vec2(127.1,311.7)),dot(p,vec2(269.5,183.3))); 
                    return fract(sin(p)*4375.5);
                }
                vec2 DoSample(in float theta, inout float r) {
                    r += 1.0 / r;
                    return (r-1.0) * vec2(cos(theta), sin(theta)) * .06;
                }
                vec4 blurSample(sampler2D tex, vec2 uvs, float radius) {
                    vec4 acc = vec4(0.0);
                    float div = 0.;
                    vec2 pixel = vec2(uResolution.y/uResolution.x, 1.0) * radius;
                    float rr = 1.0;
                    vec3 centerSample  = texture( tex, uvs ).rgb;
                    centerSample.rgb    = pow( centerSample.rgb, vec3(2.2) );

                    for (float j = 0.0; j < GOLDEN_ANGLE * ITERATIONS; j += GOLDEN_ANGLE) {
                        vec4 offsetSp = texture(tex, uvs + pixel * DoSample(j, rr));
                        offsetSp.rgb    = pow( offsetSp.rgb, vec3(2.2) );
                        acc += offsetSp;
                        div += 1.;
                    }
                    return acc / div;
                }

                vec4 blur13(sampler2D image, vec2 uv, vec2 resolution, vec2 direction) {

                    vec2 off1 = vec2(1.411764705882353) * direction;
                    vec2 off2 = vec2(3.2941176470588234) * direction;
                    vec2 off3 = vec2(5.176470588235294) * direction;
                    
                    vec4 sp0 = texture(image, uv);
                    vec4 sp1 = texture(image, uv + (off1 / resolution));
                    vec4 sp2 = texture(image, uv - (off1 / resolution));
                    vec4 sp3 = texture(image, uv + (off2 / resolution));
                    vec4 sp4 = texture(image, uv - (off2 / resolution));
                    vec4 sp5 = texture(image, uv + (off3 / resolution));
                    vec4 sp6 = texture(image, uv - (off3 / resolution));
                    
                    sp0.rgb    = pow( sp0.rgb, vec3(2.2) );
                    sp1.rgb    = pow( sp1.rgb, vec3(2.2) );
                    sp2.rgb    = pow( sp2.rgb, vec3(2.2) );
                    sp3.rgb    = pow( sp3.rgb, vec3(2.2) );
                    sp4.rgb    = pow( sp4.rgb, vec3(2.2) );
                    sp5.rgb    = pow( sp5.rgb, vec3(2.2) );
                    sp6.rgb    = pow( sp6.rgb, vec3(2.2) );
                    
                    vec4 color = vec4(0.0);
                    color += sp0 * 0.1964825501511404;
                    color += sp1 * 0.2969069646728344;
                    color += sp2 * 0.2969069646728344;
                    color += sp3 * 0.09447039785044732;
                    color += sp4 * 0.09447039785044732;
                    color += sp5 * 0.010381362401148057;
                    color += sp6 * 0.010381362401148057;

                    return color;
                }

                void main() {
                    vec2 screenUV = gl_FragCoord.xy / uResolution;
                    float aspectRatio = uResolution.x / uResolution.y;
                    vec2 correctedMouse = uMouse;
                    correctedMouse.x = (correctedMouse.x - 0.5) * aspectRatio + 0.5;
                    float distanceToMouse = length(vec2(
                        (screenUV.x - uMouse.x) * aspectRatio,
                        screenUV.y - uMouse.y
                    ));
                    float d = 1. - min(1., max(0.,  distanceToMouse / 0.3) ) ;
                    vec2 offset   =  useBlurNoise != 1  ? vec2(0.) : (r(vUv)-.5) * 2. * ( clamp(d,0., 1.)) * 0.01 ;
                    fragColor     = blurSample(tMap, vUv + offset, 0.1 * (d));
                    // fragColor = blur13(tMap, vUv + offset, uResolution, uBlurDirection * 5. * d);
                    fragColor.rgb = pow(fragColor.rgb, vec3(1./2.2));

                }

            `,
        });
        this.scene.add(this.plane);
     
        this.texture = new Texture(this.renderer.gl, {
            width: 1,
            height: 1,
        });

        await document.fonts.load(`100px "Bradford LL"`);
        await document.fonts.ready;

        this.resize();
        this.update();

        window.addEventListener('resize', this.resize);
        document.addEventListener('mousemove', this.onPointerMove, false);
        document.addEventListener('touchmove', this.onPointerMove);
        
    }
    
    onPointerMove(event) {
        let p = getPosition(this);
        const touchEvent  = TOUCH && event.touches ? (event.touches[0] || event.changedTouches[0]) : event;
        this.mouseObjX = touchEvent.pageX - p.left;
        this.mouseObjY = touchEvent.pageY - p.top;
    }

    update() {

        requestAnimationFrame(()=>{
            this.update();
        });


        this.mouseObj.x += (this.mouseObjX - this.mouseObj.x)  * 0.1;
        this.mouseObj.y += ((this.mouseObjY - this.scroll) - this.mouseObj.y)  * 0.1;

        // this.titles.forEach( el => el.update() );

        this.time += 0.2;
        this.camera.position[2] =  (this.resolution[1]) / 2 / Math.tan(Math.PI * 45 / 360);

        this.camera.updateMatrix();
        this.camera.updateWorldMatrix();
        
        this.plane.scale[0] = this.resolution[0];
        this.plane.scale[1] = this.resolution[1];  
        this.plane.scale[2] = 1
        this.plane.updateMatrix();
        this.plane.updateWorldMatrix();
        this.plane.material.setUniform('uResolution', this.resolution);
        this.plane.material.setUniform('uCameraPosition', [this.camera.worldMatrix[12],this.camera.worldMatrix[13],this.camera.worldMatrix[14]]);
        this.plane.material.setUniform('uDistortionScale',     0.3);
        this.plane.material.setUniform('uChromaticAberration', 0.0);
        this.plane.material.setUniform('uBlurStrength',        0.0);  

        this.plane.material.setUniform('uMouse', [ 
            (this.mouseObj.x * DPR) / this.resolution[0], 
            1.0 - (this.mouseObj.y * DPR) / this.resolution[1] 
        ]);


        this.plane.material.setUniform('tMap', this.texture);
        this.plane.material.setUniform('useBlurNoise', 1);
        this.renderer.clearColor(0,0,0,1);
        this.renderer.clear();
        this.renderer.render(this.scene, this.camera );

    }

    async loadFont() {
        await document.fonts.load(`${this.w * 0.7}px "Bradford LL"`);
        await document.fonts.ready;
    }

   async drawCanvasTexture() {
        const W = this.w * DPR;
        const H = this.h * DPR;

        const text = "2";
        const fontFamily = `"Bradford LL"`;

        const topMargin = 100;
        const bottomMargin = H * 0.2;
        const availableTextHeight = H - topMargin - bottomMargin;

        await document.fonts.load(`100px ${fontFamily}`);
        await document.fonts.ready;

        const gradient = this.ctx.createLinearGradient(0, 0, 0, H);
        gradient.addColorStop(0, '#f6ebe5'); // top
        gradient.addColorStop(1, '#fdf9ee'); // bottom
        this.ctx.fillStyle = gradient;
        this.ctx.fillRect(0, 0, W, H);

        let fontSize = availableTextHeight;

        this.ctx.font = `${fontSize}px ${fontFamily}`;

        let metrics = this.ctx.measureText(text);
        let textHeight =
            metrics.actualBoundingBoxAscent +
            metrics.actualBoundingBoxDescent;

        if (textHeight > 0) {
            fontSize *= availableTextHeight / textHeight;
        }

        this.ctx.font = `${fontSize}px ${fontFamily}`;
        metrics = this.ctx.measureText(text);

        if (metrics.width > W * 0.9) {
            fontSize *= (W * 0.9) / metrics.width;
            this.ctx.font = `${fontSize}px ${fontFamily}`;
            metrics = this.ctx.measureText(text);
        }

        const textHeightFinal =
            metrics.actualBoundingBoxAscent +
            metrics.actualBoundingBoxDescent;

        const availableTop = topMargin;
        const availableBottom = H;

        const availableCenter =
            availableTop + (availableBottom - availableTop) / 2;

        const y =
            availableCenter +
            (metrics.actualBoundingBoxAscent - metrics.actualBoundingBoxDescent) / 2;

        this.ctx.fillStyle = '#9f7e58';
        this.ctx.textAlign = 'center';
        this.ctx.textBaseline = 'alphabetic';

        this.ctx.fillText(text, W / 2, y);

        this.texture.bindImage(this.$canvas);
    }

    resize() {
        let w = this.clientWidth;
        let h = this.clientHeight;

        this.renderer?.setPixelRatio(DPR);
        this.renderer?.resize(w, h);

        this.resolution = [w * DPR, h * DPR];

        this.camera.aspect = w / h;
        this.camera.updateProjectionMatrix();

        this.$canvas.width = w * DPR;
        this.$canvas.height = h * DPR;

        this.w = w;
        this.h = h;

        this.drawCanvasTexture();
    }

}

customElements.define('gl-blur-number', GLBlurNumber);